import React, { useState, useEffect } from 'react';
import { Camera, Plus, Lock, Globe, Upload, LogOut, User, Grid, List, Crown } from 'lucide-react';
import AlbumGrid from './AlbumGrid';
import PhotoUploader from './PhotoUploader';
import AlbumViewer from './AlbumViewer';
import CreateAlbumModal from './CreateAlbumModal';
import AdminAlbumManager from './AdminAlbumManager';
import './Gallery.css';
import authService from '../services/authService';
import { getApiUrl } from '../utils/config';

const Gallery = ({ onBack }) => {
  const [user, setUser] = useState(null);
  const [albums, setAlbums] = useState([]);
  const [publicAlbums, setPublicAlbums] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [showUploader, setShowUploader] = useState(false);
  const [showCreateAlbum, setShowCreateAlbum] = useState(false);
  const [activeTab, setActiveTab] = useState('public'); // Start with public by default
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [authLoading, setAuthLoading] = useState(true);
  const [showAdminManager, setShowAdminManager] = useState(false);

  useEffect(() => {
    initializeApp();
  }, []);

  useEffect(() => {
    // Fetch albums when user changes
    if (user) {
      fetchMyAlbums();
      setActiveTab('my-albums'); // Switch to my albums if user is logged in
    }
  }, [user]);

  const initializeApp = async () => {
    setAuthLoading(true);
    try {
      // Always fetch public albums first, regardless of auth status
      console.log('Initializing app...');
      
      // Fetch public albums immediately without waiting for auth
      await fetchPublicAlbums();
      
      // Then check auth status (but don't let it block public access)
      await checkAuthStatus();
      
    } catch (error) {
      console.error('Error initializing app:', error);
      // Still try to fetch public albums even if there's an error
      try {
        await fetchPublicAlbums();
      } catch (publicError) {
        console.error('Failed to fetch public albums as fallback:', publicError);
        setPublicAlbums([]);
      }
    } finally {
      setAuthLoading(false);
      setLoading(false);
    }
  };

  const checkAuthStatus = async () => {
    try {
      console.log('Checking auth status...');
      
      // First check for OAuth callback
      const hasAuth = await authService.handleOAuthCallback();
      
      if (hasAuth || authService.isAuthenticated()) {
        const userData = authService.getUser();
        if (userData) {
          setUser(userData);
          console.log('User authenticated via JWT:', userData.username);
        } else {
          // Try to get user from server
          try {
            const response = await authService.authenticatedFetch(
              `${getApiUrl()}/auth/status`
            );
            if (response.ok) {
              const data = await response.json();
              if (data.authenticated && data.user) {
                setUser(data.user);
                console.log('User authenticated via server:', data.user.username);
                // Update stored user data
                authService.setAuth(
                  authService.getAccessToken(),
                  authService.getRefreshToken(),
                  data.user
                );
              }
            }
          } catch (authError) {
            console.log('Auth check failed, user not logged in');
          }
        }
      } else {
        // FIXED: For mobile compatibility, try session-based auth but don't let it block public access
        try {
          const response = await fetch(`${getApiUrl()}/auth/status`, {
            credentials: 'include'
          });
          const data = await response.json();
          
          if (data.authenticated) {
            setUser(data.user);
            console.log('User authenticated via session:', data.user.username);
            // Try to exchange session for JWT token
            await authService.exchangeSessionForToken();
          } else {
            console.log('No user session found');
          }
        } catch (sessionError) {
          // Don't block the app if session check fails
          console.log('Session check failed, continuing without auth');
        }
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      // Don't block the app if auth check fails
    }
  };

  const fetchMyAlbums = async () => {
    if (!user && !authService.isAuthenticated()) return;
    
    try {
      const response = await authService.authenticatedFetch(
        `${getApiUrl()}/api/albums/my-albums`
      );
      
      if (response.ok) {
        const data = await response.json();
        setAlbums(data);
      } else {
        console.error('Failed to fetch my albums');
      }
    } catch (error) {
      console.error('Error fetching my albums:', error);
    }
  };

  // FIXED: Properly structured fetchPublicAlbums function
  const fetchPublicAlbums = async () => {
    try {
      const url = `${getApiUrl()}/api/albums/public`;
      
      console.log('Fetching public albums from:', url);
      
      // FIXED: Always use regular fetch for public albums to avoid auth issues on mobile
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          // Don't send Authorization header for public content
        },
        // Important: Don't include credentials for public requests
        credentials: 'omit'
      });
      
      console.log('Public albums response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Public albums data:', data);
        setPublicAlbums(data);
      } else {
        console.error('Failed to fetch public albums:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('Error details:', errorText);
        // Set empty array on error to prevent infinite loading
        setPublicAlbums([]);
      }
    } catch (error) {
      console.error('Error fetching public albums:', error);
      // Set empty array on error to prevent infinite loading
      setPublicAlbums([]);
    }
  };

  const handleLogin = () => {
    // Use current domain for auth to ensure it works in all environments
    window.location.href = `${getApiUrl()}/auth/discord`;
  };

  const handleLogout = async () => {
    await authService.logout();
    // The page will redirect, no need to update state
  };

  const handleCreateAlbum = async (albumData) => {
    try {
      const response = await authService.authenticatedFetch(
        `${getApiUrl()}/api/albums`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(albumData)
        }
      );

      if (response.ok) {
        const newAlbum = await response.json();
        setAlbums([newAlbum, ...albums]);
        setShowCreateAlbum(false);
        setSelectedAlbum(newAlbum);
        setShowUploader(true);
      } else {
        const errorData = await response.json();
        console.error('Failed to create album:', errorData.error);
        alert(`Failed to create album: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error creating album:', error);
      alert('Error creating album. Please try again.');
    }
  };
  
  const handleDeleteAlbum = (albumId) => {
    setAlbums(prevAlbums => prevAlbums.filter(album => album.id !== albumId));
    setPublicAlbums(prevAlbums => prevAlbums.filter(album => album.id !== albumId));
  };

  // Enhanced handleUploadComplete with delay for B2 processing
  const handleUploadComplete = async () => {
    console.log('Upload completed, waiting for B2 processing...');
    setShowUploader(false);
    
    // Show a loading message in the album viewer while we wait
    if (selectedAlbum) {
      // Add a loading state to indicate we're waiting for photos to be available
      setLoading(true);
    }
    
    // Wait 7 seconds to ensure B2 has processed the uploads and thumbnails are ready
    await new Promise(resolve => setTimeout(resolve, 7000));
    
    try {
      // Refresh albums first
      await Promise.all([
        fetchMyAlbums(),
        fetchPublicAlbums()
      ]);
      
      // If we're viewing an album, refresh its photos specifically
      if (selectedAlbum) {
        console.log('Refreshing album photos after upload...');
        
        // Fetch the updated album with new photo count
        const response = await authService.authenticatedFetch(
          `${getApiUrl()}/api/albums/${selectedAlbum.id}`
        );
        
        if (response.ok) {
          const updatedAlbum = await response.json();
          setSelectedAlbum(updatedAlbum);
        }
        
        // Force refresh of the AlbumViewer by temporarily clearing and resetting the selected album
        const currentAlbum = selectedAlbum;
        setSelectedAlbum(null);
        
        // Small delay to ensure state update
        await new Promise(resolve => setTimeout(resolve, 100));
        
        setSelectedAlbum(currentAlbum);
      }
    } catch (error) {
      console.error('Error refreshing after upload:', error);
    } finally {
      setLoading(false);
    }
    
    console.log('Album refresh completed');
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedAlbum(null); // Clear selected album when switching tabs
  };

  const handleAdminManagerClose = async () => {
    setShowAdminManager(false);
    // Refresh albums in case any were modified by admin
    try {
      await Promise.all([
        fetchMyAlbums(),
        fetchPublicAlbums()
      ]);
    } catch (error) {
      console.error('Error refreshing albums after admin changes:', error);
    }
  };

  if (authLoading) {
    return (
      <div className="gallery-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gallery-container">
      <header className="gallery-header">
        <div className="header-content">
          <div className="header-left">
            <Camera size={32} className="gallery-logo" />
            <h1>Gallery</h1>
          </div>
          
          <div className="header-right">
            {user ? (
              <>
                <button 
                  className="btn-primary"
                  onClick={() => setShowCreateAlbum(true)}
                >
                  <Plus size={20} />
                  New Album
                </button>
                {user.is_admin && (
                  <button 
                    className="btn-admin"
                    onClick={() => setShowAdminManager(true)}
                    title="Admin: Manage All Albums"
                  >
                    <Crown size={20} />
                    Admin
                  </button>
                )}
                <div className="user-menu">
                  <img 
                    src={`https://cdn.discordapp.com/avatars/${user.discord_id}/${user.avatar}.png`}
                    alt={user.username}
                    className="user-avatar"
                  />
                  <span>{user.username}</span>
                  <button onClick={handleLogout} className="btn-icon" title="Logout">
                    <LogOut size={20} />
                  </button>
                </div>
              </>
            ) : (
              <button className="btn-discord" onClick={handleLogin}>
                <User size={20} />
                Login with Discord
              </button>
            )}
          </div>
        </div>
      </header>

      <nav className="gallery-nav">
        <div className="nav-tabs">
          {user && (
            <button
              className={`nav-tab ${activeTab === 'my-albums' ? 'active' : ''}`}
              onClick={() => handleTabChange('my-albums')}
            >
              <Lock size={18} />
              My Albums
            </button>
          )}
          <button
            className={`nav-tab ${activeTab === 'public' ? 'active' : ''}`}
            onClick={() => handleTabChange('public')}
          >
            <Globe size={18} />
            Public Albums
          </button>
        </div>

        <div className="view-toggle">
          <button
            className={`btn-icon ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
            title="Grid view"
          >
            <Grid size={20} />
          </button>
          <button
            className={`btn-icon ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
            title="List view"
          >
            <List size={20} />
          </button>
        </div>
      </nav>

      <main className="gallery-main">
        {loading ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading albums...</p>
          </div>
        ) : selectedAlbum ? (
          <AlbumViewer
            album={selectedAlbum}
            onBack={() => setSelectedAlbum(null)}
            onUpload={() => setShowUploader(true)}
            canUpload={user && (selectedAlbum.user_id === user.id || selectedAlbum.user_permission === 'upload')}
            onDeleteAlbum={handleDeleteAlbum}
          />
        ) : (
          <AlbumGrid
            albums={activeTab === 'my-albums' ? albums : publicAlbums}
            viewMode={viewMode}
            onSelectAlbum={setSelectedAlbum}
            onDeleteAlbum={handleDeleteAlbum}
            currentUser={user}
            emptyMessage={
              activeTab === 'my-albums' 
                ? "You don't have any albums yet. Create one to get started!"
                : "No public albums available."
            }
          />
        )}
      </main>

      {showCreateAlbum && (
        <CreateAlbumModal
          onClose={() => setShowCreateAlbum(false)}
          onCreate={handleCreateAlbum}
        />
      )}

      {showUploader && selectedAlbum && (
        <PhotoUploader
          albumId={selectedAlbum.id}
          onClose={() => setShowUploader(false)}
          onUploadComplete={handleUploadComplete}
        />
      )}

      {showAdminManager && user?.is_admin && (
        <AdminAlbumManager
          onClose={handleAdminManagerClose}
        />
      )}
    </div>
  );
};

export default Gallery;